import React from 'react';
import { useApp } from '../context/AppContext';
import { 
  ArrowRight, 
  Star, 
  User, 
  Globe, 
  CheckCircle2, 
  Radio,
  ExternalLink
} from 'lucide-react';

export const ProjectsView: React.FC = () => {
  const { 
    projects, 
    criteria, 
    draftScores, 
    setActiveTab, 
    hasVoted 
  } = useApp();

  // Calculate judge progress
  const scoredCount = projects.filter((p) => {
    const pScores = draftScores[p.id] || {};
    return criteria.every((c) => (pScores[c.id] || 0) > 0);
  }).length;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12 pb-24">
      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 border-b border-brand-tan pb-6">
        <div>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-red/10 text-brand-red text-xs font-bold uppercase tracking-wider border border-brand-red/20">
              <Radio className="w-3.5 h-3.5 animate-pulse" />
              <span>NEXT Ventures Pitch Showcase</span>
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-brand-black tracking-tight">
            12 Ministry Finalists
          </h1>
          <p className="mt-2 text-sm sm:text-base text-brand-subtext max-w-2xl">
            Explore the 12 innovative ministries pitching at Shark Tank. View more information in the{' '}
            <a
              href="https://www.flipsnack.com/multiplication/2026-shark-tank-program"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-red font-semibold hover:underline inline-flex items-center gap-0.5"
            >
              program book
              <ExternalLink className="w-3.5 h-3.5 inline ml-0.5 opacity-80" />
            </a>
            .
          </p>
        </div>

        {/* Voting Progress Pill */}
        <div className="bg-white border border-brand-tan px-4 py-3 rounded-2xl flex items-center gap-3 shrink-0 shadow-sm">
          <div className="w-10 h-10 rounded-xl bg-brand-red/10 border border-brand-red/30 flex items-center justify-center">
            <Star className="w-5 h-5 text-brand-red" />
          </div>
          <div>
            <div className="text-xs text-brand-subtext font-semibold">Judge Ballot Status</div>
            <div className="text-sm font-extrabold text-brand-black">
              {hasVoted ? (
                <span className="text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Ballot Submitted
                </span>
              ) : (
                <span>
                  {scoredCount} of {projects.length} evaluated
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project) => {
          const pScores = draftScores[project.id] || {};
          const isFullyScored = criteria.every((c) => (pScores[c.id] || 0) > 0);
          const currentTotal = criteria.reduce((sum, c) => sum + (pScores[c.id] || 0), 0);
          const maxPossible = criteria.length * 5;

          return (
            <div
              key={project.id}
              className="bg-white border border-brand-tan hover:border-brand-warmgray rounded-3xl p-6 flex flex-col justify-between transition-all hover:shadow-md group relative overflow-hidden shadow-sm"
            >
              {/* Top Accent Line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-red to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

              <div>
                {/* Meta Header */}
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="text-xs font-extrabold px-2.5 py-0.5 rounded-full bg-brand-red/10 text-brand-red border border-brand-red/20">
                    #{project.order}
                  </span>

                  {project.website && (
                    <a
                      href={project.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[11px] text-brand-subtext hover:text-brand-red flex items-center gap-1 font-medium transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Globe className="w-3 h-3" />
                      <span className="truncate max-w-[120px]">{project.website.replace(/^https?:\/\//, '')}</span>
                      <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                    </a>
                  )}
                </div>

                {/* Title & Presenter */}
                <h3 className="text-lg font-extrabold text-brand-black group-hover:text-brand-red transition-colors leading-tight">
                  {project.title}
                </h3>

                <div className="flex items-center gap-1.5 text-xs text-brand-subtext font-semibold mt-1">
                  <User className="w-3.5 h-3.5 text-brand-red" />
                  <span>{project.presenter}</span>
                </div>

                {/* Purpose */}
                <p className="text-xs text-brand-subtext mt-3 leading-relaxed">
                  <strong className="text-brand-black font-semibold">Purpose: </strong>
                  {project.purpose}
                </p>
              </div>

              {/* Bottom Strip: Score Status & Rate Action */}
              <div className="mt-6 pt-4 border-t border-brand-tan/50 flex items-center justify-between gap-2">
                <div>
                  {isFullyScored ? (
                    <span className="text-[11px] font-bold text-emerald-800 bg-emerald-100 px-2.5 py-1 rounded-lg border border-emerald-300 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      {currentTotal}/{maxPossible} pts
                    </span>
                  ) : currentTotal > 0 ? (
                    <span className="text-[11px] font-medium text-amber-900 bg-amber-100 px-2 py-1 rounded-lg border border-amber-300">
                      {currentTotal} pts
                    </span>
                  ) : (
                    <span className="text-xs text-brand-subtext font-medium">
                      Not rated
                    </span>
                  )}
                </div>

                <button
                  onClick={() => setActiveTab('vote')}
                  className="px-4 py-2 rounded-xl bg-brand-red hover:bg-red-600 text-white text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 hover:scale-105"
                >
                  <span>Rate Ministry</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
